import { Field, FormFieldTest } from "../types/types";

export type PhoneDependencyInfo = {
    countryFieldId?: number;
    countryFieldAccessKey?: string;
    countryFieldLabel?: string;
    countryCodeFieldId?: number;
    countryCodeFieldAccessKey?: string;
    countryCodeFieldLabel?: string;
    countryNameFieldId?: number;
    countryNameFieldAccessKey?: string;
    countryNameFieldLabel?: string;
};

type PhoneFieldCarrier = Field | Partial<FormFieldTest> | { field?: Field | null } | null | undefined;

type PhoneFieldClassification = {
    key: string;
    kind: "number" | "countryName" | "countryCode" | "extension";
    label: string;
};

const PHONE_GROUP_REMOVE_REGEX = /\b(country|extension|ext|code)\b/g;
const WHITESPACE_REGEX = /\s+/g;

const isField = (candidate: any): candidate is Field => {
    return Boolean(candidate && typeof candidate === "object" && "accessKey" in candidate && "fieldType" in candidate);
};

const getFieldFromCarrier = (carrier: PhoneFieldCarrier): Field | null | undefined => {
    if (!carrier) {
        return null;
    }

    if (isField(carrier)) {
        return carrier;
    }

    if (typeof carrier === "object" && "field" in carrier) {
        return (carrier as any).field as Field | null | undefined;
    }

    return null;
};

const buildPhoneFieldText = (field?: Field | null): string => {
    if (!field) {
        return "";
    }

    return [
        field.name,
        field.label,
        field.config?.uiMeta?.displayName,
        field.config?.uiMeta?.fieldLabel,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
};

export const classifyPhoneField = (carrier: PhoneFieldCarrier): PhoneFieldClassification | null => {
    const field = getFieldFromCarrier(carrier);
    if (!field?.id) {
        return null;
    }

    const text = buildPhoneFieldText(field);
    if (!text || !text.includes("phone")) {
        return null;
    }

    const fieldType = (field.fieldType || "").toUpperCase();
    const isPhoneFieldType = fieldType.includes("PHONE");

    const containsCountry = /\bcountry\b/.test(text);
    const containsCodeWord = /\bcode\b/.test(text) || text.includes("country code");
    let kind: PhoneFieldClassification["kind"];
    if (containsCountry && containsCodeWord) {
        kind = "countryCode";
    } else if (containsCountry) {
        kind = "countryName";
    } else if (/\b(extension|ext)\b/.test(text)) {
        kind = "extension";
    } else {
        kind = "number";
    }

    if ((kind === "number" || kind === "extension") && !isPhoneFieldType) {
        return null;
    }

    const baseKey = text
        .replace(PHONE_GROUP_REMOVE_REGEX, "")
        .replace(WHITESPACE_REGEX, " ")
        .trim();

    if (!baseKey) {
        return null;
    }

    const label = field.config?.uiMeta?.displayName || field.config?.uiMeta?.fieldLabel || field.name || "Phone Country";

    return { key: baseKey, kind, label };
};

export const buildPhoneDependencyMap = (
    carriers: Array<PhoneFieldCarrier> | undefined | null
): Map<number, PhoneDependencyInfo> => {
    const map = new Map<number, PhoneDependencyInfo>();
    if (!carriers?.length) {
        return map;
    }

    const groups = new Map<string, {
        numbers: Field[];
        countryName?: { field: Field; label: string };
        countryCode?: { field: Field; label: string };
    }>();

    carriers.forEach((carrier) => {
        const classification = classifyPhoneField(carrier);
        if (!classification) {
            return;
        }

        const field = getFieldFromCarrier(carrier);
        if (!field?.id) {
            return;
        }

        const entry = groups.get(classification.key) ?? { numbers: [] };
        if (classification.kind === "number") {
            entry.numbers.push(field);
        } else if (classification.kind === "countryName") {
            entry.countryName = { field, label: classification.label };
        } else if (classification.kind === "countryCode") {
            entry.countryCode = { field, label: classification.label };
        }
        groups.set(classification.key, entry);
    });

    groups.forEach(({ countryName, countryCode, numbers }) => {
        if (!numbers.length) {
            return;
        }

        const preferredCountry = countryCode ?? countryName;
        if (!preferredCountry?.field?.id) {
            return;
        }

        numbers.forEach((numberField) => {
            if (!numberField?.id) {
                return;
            }

            map.set(numberField.id, {
                countryFieldId: preferredCountry.field.id,
                countryFieldAccessKey: preferredCountry.field.accessKey,
                countryFieldLabel: preferredCountry.label,
                countryCodeFieldId: countryCode?.field?.id,
                countryCodeFieldAccessKey: countryCode?.field?.accessKey,
                countryCodeFieldLabel: countryCode?.label,
                countryNameFieldId: countryName?.field?.id,
                countryNameFieldAccessKey: countryName?.field?.accessKey,
                countryNameFieldLabel: countryName?.label,
            });
        });
    });

    return map;
};
